// Notifications 前端 hooks — 消费 /api/v1/notifications
// - useNotifications：取最新 20 条 + unread 计数（staleTime 30s / refetch 30s，对齐 Topbar 节奏）
// - useMarkNotificationRead：标记单条已读（乐观 + invalidate）
//
// Topbar 铃铛 + Dashboard 右下角通知区块都消费这套 hooks。

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api.js';

import type {
  NotificationKind,
  NotificationListResult,
} from '@aliashub/shared/dto';

export type {
  NotificationDTO,
  NotificationListResult,
  NotificationKind,
  NotificationSeverity,
} from '@aliashub/shared/dto';

const NOTIF_KEY = ['notifications', 'summary'] as const;

export function useNotifications() {
  return useQuery<NotificationListResult>({
    queryKey: NOTIF_KEY,
    queryFn: () => api.get<NotificationListResult>('/api/v1/notifications'),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => api.post<void>(`/api/v1/notifications/${id}/read`),
    onMutate: async (id) => {
      // 乐观：把这一条 readAt 置 now，unread -1
      await qc.cancelQueries({ queryKey: NOTIF_KEY });
      const cur = qc.getQueryData<NotificationListResult>(NOTIF_KEY);
      if (cur) {
        const nextItems = cur.items.map((it) =>
          it.id === id ? { ...it, readAt: Math.floor(Date.now() / 1000) } : it,
        );
        const wasUnread = cur.items.find((it) => it.id === id)?.readAt == null;
        qc.setQueryData<NotificationListResult>(NOTIF_KEY, {
          items: nextItems,
          unread: Math.max(0, cur.unread - (wasUnread ? 1 : 0)),
        });
      }
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: NOTIF_KEY });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => api.post<void>('/api/v1/notifications/read-all'),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: NOTIF_KEY });
      const cur = qc.getQueryData<NotificationListResult>(NOTIF_KEY);
      if (cur) {
        const now = Math.floor(Date.now() / 1000);
        qc.setQueryData<NotificationListResult>(NOTIF_KEY, {
          items: cur.items.map((it) =>
            it.readAt == null ? { ...it, readAt: now } : it,
          ),
          unread: 0,
        });
      }
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: NOTIF_KEY });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIF_KEY });
    },
  });
}

// ---- NotificationKind → 文案映射（Topbar panel + Dashboard 共用）----

export const NOTIF_KIND_LABEL: Record<NotificationKind, string> = {
  alias_created: '新别名',
  alias_expiring: '即将过期',
  alias_expired: '已过期',
  forward_failed: '转发失败',
  webhook_failed: 'Webhook 失败',
  attachment_uploaded: '附件上传',
  domain_verified: '域名验证',
  system: '系统',
};
